/**
 * Block 12 PR-D-A Phase D5 Step 9 — useQuoteDraftStore (Zustand).
 *
 * In-memory draft store для активного КП. Keyed by `draftKey` —
 * `projectId` если КП пришёл из калькуляторной анкеты, иначе литерал
 * `'standalone'` (КП открыт через прямой URL без `?projectId=`).
 *
 * Mirror hotfix-2C Phase C5 Step 10 (`useCalculatorStore`) — два слоя:
 *   - Layer 1 (this module): fast in-memory dictionary, sync lookup —
 *     переживает SPA navigation (cross-route FindPriceButton flows).
 *   - Layer 2 (`quoteDraftIdbSync` Step 9b): durable IDB mirror —
 *     переживает F5 / browser restart / cross-tab.
 *   - Layer 3 wiring (`QuotePageContainer` Step 9c): cascade
 *     Zustand → IDB → seed (Step 8) → blank + debounced 300ms autosave.
 *
 * **Key strategy:** `string` draftKey (не CalculatorAnketaType как в C5).
 * Один draft на ключ; setDraft перезаписывает.
 *
 * **No persist middleware:** Zustand persist → localStorage; для QuoteState
 * (state с массивами compositionRows/conditions/contacts) IDB подходит лучше
 * (отдельный модуль quoteDraftIdbSync). Этот store — только in-memory.
 *
 * Reference: Block_12_PR_D_A_2026-05-17_start_v2.md Phase D5 Step 9.
 */

import { create } from 'zustand';
import type { QuoteState } from '../types';

export interface QuoteDraftStore {
  /** Map draftKey → последний QuoteState. Partial — drafts заводятся по требованию. */
  drafts: Partial<Record<string, QuoteState>>;

  /** Перезаписывает draft под ключом. Sync. */
  setDraft: (key: string, state: QuoteState) => void;
  /** Lookup draft. `undefined` если нет. Sync. */
  getDraft: (key: string) => QuoteState | undefined;
  /** Удаляет draft (e.g. после успешного save в saved-quotes). Sync. */
  clearDraft: (key: string) => void;
  /** Полная очистка (e.g. logout). Sync. */
  clearAll: () => void;
}

export const useQuoteDraftStore = create<QuoteDraftStore>((set, get) => ({
  drafts: {},

  setDraft: (key, state) => set((s) => ({ drafts: { ...s.drafts, [key]: state } })),

  getDraft: (key) => get().drafts[key],

  clearDraft: (key) =>
    set((s) => {
      if (!(key in s.drafts)) return s;
      const next = { ...s.drafts };
      delete next[key];
      return { drafts: next };
    }),

  clearAll: () => set({ drafts: {} }),
}));

/** Тестовый утилит — сброс store. Используется в beforeEach test cleanup. */
export function resetQuoteDraftStoreForTests(): void {
  useQuoteDraftStore.setState({ drafts: {} });
}
